import { AxiosResponse } from "axios";
import axiosClient from "./axios-client"; // Import the axiosClient instance

// Profile API Endpoint
const GET_PROFILE_URL = "/Settings/Profile";

const UPDATE_PRIVACY_URL = "/Settings/UpdatePrivacy";

const GET_NON_REV_AIRLINES_URL = "/Settings/GetNonRevAirlines";

const GET_PRIVACY_URL = "/Settings/GetPrivacy";

const SET_ANONYMOUS_STATUS_URL = "/Settings/AddAnonymousStatus";

const GET_AIRLINES_URL = "/User/airlines";

const EXCLUDE_INCLUDE_AIRLINES_URL = "/Settings/ExcludeIncludeAirlines";

const GET_EXCLUDED_AIRLINES_URL = "/Settings/GetExcludedAirlines";

const UPDATE_PROFILE_URL = "/Settings/UpdateProfile";

const DELETE_ACCOUNT_URL = "/Settings/DeleteAccount";

const ADD_PROFILE_PICTURE_URL = "/Settings/AddProfilePicture";

const GET_PROFILE_PICTURE_URL = "/Settings/GetProfilePicture";

interface UpdateProfilePayload {
  fullName: string;
  primaryAirlineId: number;
  homebaseId: number;
  additionalAirlineIds: number[];
}

/**
 * Fetch the user's profile data.
 * @param userId The ID of the user whose profile data is to be fetched.
 * @returns The user's profile data.
 */
export const getUserProfile = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_PROFILE_URL);
    return response; // Return the entire response (no need to access .data)
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching user profile:", error.message);
    } else {
      console.error("Unknown error fetching user profile");
    }
    throw error;
  }
};

export const updateUserPrivacy = async (displayName: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.put(`${UPDATE_PRIVACY_URL}?DisplayName=${encodeURIComponent(displayName)}`);
    return response; // Return the entire response
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error updating privacy settings:", error.message);
    } else {
      console.error("Unknown error updating privacy settings");
    }
    throw error;
  }
};

export const getNonRevAirlines = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_NON_REV_AIRLINES_URL);
    return response; // Return the data from the response
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching non-rev airlines:", error.message);
    } else {
      console.error("Unknown error fetching non-rev airlines");
    }
    throw error;
  }
};

export const getPrivacySettings = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_PRIVACY_URL);
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching privacy settings:", error.message);
    } else {
      console.error("Unknown error fetching privacy settings");
    }
    throw error;
  }
};

export const setAnonymousStatus = async (isAnonymous: boolean): Promise<any> => {
  try {
    // Sending the request to the server to update the anonymous status
    const response = await axiosClient.get(`${SET_ANONYMOUS_STATUS_URL}?isAnonymous=${isAnonymous}`);
    return response; // Assuming the API response contains the updated status or success message
  } catch (error) {
    console.error("Error updating anonymous status:", error);
    throw new Error("Failed to update anonymous status.");
  }
};

export const getAirlines = async (searchQuery: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(`${GET_AIRLINES_URL}?search=${encodeURIComponent(searchQuery)}`);
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching airlines:", error.message);
    } else {
      console.error("Unknown error fetching airlines");
    }
    throw error;
  }
};

export const excludeIncludeAirlines = async (airlineIds: number[], isExcluded: boolean): Promise<any> => {
  try {
    const queryParams = airlineIds.map((id) => `AirlineIds=${id}`).join("&");
    const response: AxiosResponse<any> = await axiosClient.put(`${EXCLUDE_INCLUDE_AIRLINES_URL}?${queryParams}&IsExcluded=${isExcluded}`);
    return response;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error excluding or including airlines:", error.message);
    } else {
      console.error("Unknown error excluding or including airlines");
    }
    throw error;
  }
};

export const getExcludedAirlines = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_EXCLUDED_AIRLINES_URL);
    return response;
  } catch (error) {
    console.error("Error fetching excluded airlines:", error);
    throw error;
  }
};

export const updateUserProfile = async (payload: UpdateProfilePayload): Promise<any> => {
  try {
    const response = await axiosClient.put(UPDATE_PROFILE_URL, payload);
    return response; // Or return response.data if you're using just the data
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const deleteAccount = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(DELETE_ACCOUNT_URL);

    return response;
  } catch (error) {
    console.error("Error deleting account:", error);
    throw error;
  }
};

// Feedback API Endpoints
const GET_FEEDBACK_TYPES_URL = "/Settings/GetFeedbackTypes";
const ADD_FEEDBACK_URL = "/Settings/AddFeedback";

export interface FeedbackType {
  id: number;
  typeName: string;
}

/**
 * Fetch available feedback types
 * @returns Array of feedback types
 */
export const getFeedbackTypes = async (): Promise<FeedbackType[]> => {
  try {
    const response = (await axiosClient.get(GET_FEEDBACK_TYPES_URL)) as unknown as FeedbackType[];
    return response;
  } catch (error) {
    console.error("Error fetching feedback types:", error);
    throw error;
  }
};

/**
 * Submit user feedback with optional image
 * @param formData FormData containing FeedbackComment, Image (optional), and FeedbackTypeId
 * @returns API response
 */
export const submitFeedback = async (formData: FormData): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.post(ADD_FEEDBACK_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  } catch (error) {
    console.error("Error submitting feedback:", error);
    throw error;
  }
};

export const updateProfilePicture = async (file: File): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append("file", file); 

    const response = await axiosClient.post(ADD_PROFILE_PICTURE_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response;
  } catch (error) {
    console.error(error instanceof Error ? "Error updating profile picture: " + error.message : "Unknown error updating profile picture");
    throw error;
  }
};

export const getProfilePicture = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosClient.get(GET_PROFILE_PICTURE_URL);
    return response; // Return the data from the response
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching non-rev airlines:", error.message);
    } else {
      console.error("Unknown error fetching non-rev airlines");
    }
    throw error;
  }
};
